import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import TeamItem from './TeamItem';
import TeamMemberItem from './TeamMemberItem';
import { useSidebar } from './useSidebar';
import { Team } from './types';
import AddTeamMemberDialog from './AddTeamMemberDialog';
import { useToast } from '@/hooks/use-toast';
import { TeamMember } from './types';

interface TeamsSectionProps {
  teams: Team[];
  setTeams: React.Dispatch<React.SetStateAction<Team[]>>;
  setIsNewTeamDialogOpen: (isOpen: boolean) => void;
}

const TeamsSection: React.FC<TeamsSectionProps> = ({ 
  teams, 
  setTeams,
  setIsNewTeamDialogOpen 
}) => {
  const { expandedTeams, toggleTeam } = useSidebar();
  const navigate = useNavigate();
  const [isAddMemberDialogOpen, setIsAddMemberDialogOpen] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const { toast } = useToast();

  const handleMemberClick = (memberId: string, memberName: string) => {
    navigate(`/team-member/${memberId}`, { 
      state: { memberName } 
    });
  };

  const handleAddMemberClick = (teamId: string) => {
    setSelectedTeamId(teamId);
    setIsAddMemberDialogOpen(true);
  };

  const handleAddMember = (member: TeamMember) => {
    if (!selectedTeamId) return;

    setTeams(prevTeams => 
      prevTeams.map(team => 
        team.id === selectedTeamId 
          ? { ...team, members: [...team.members, member] } 
          : team
      )
    );
  };

  return (
    <>
      <div className="mt-8 px-4">
        <div className="text-xs font-semibold text-muted-foreground mb-2">TEAMS</div>
        <ul className="space-y-1">
          {teams.map(team => (
            <React.Fragment key={team.id}>
              <TeamItem 
                name={team.name}
                isExpanded={expandedTeams[team.id] || false}
                isActive={false}
                onClick={() => toggleTeam(team.id)}
              />
              
              {expandedTeams[team.id] && (
                <div className="ml-6 mt-1 mb-2 space-y-1">
                  {team.members.map(member => (
                    <TeamMemberItem 
                      key={member.id}
                      member={member}
                      onClick={handleMemberClick}
                    />
                  ))}
                  <Button
                    variant="ghost" 
                    size="sm" 
                    className="w-full h-6 text-xs justify-start text-muted-foreground hover:text-foreground px-2"
                    onClick={() => handleAddMemberClick(team.id)}
                  >
                    + Add member
                  </Button>
                </div>
              )}
            </React.Fragment>
          ))}
          <li className="mt-2">
            <Button 
              variant="ghost" 
              size="sm" 
              className="w-full h-7 text-xs justify-start text-muted-foreground hover:text-foreground px-2 gap-1"
              onClick={() => setIsNewTeamDialogOpen(true)}
            >
              <Users className="h-3.5 w-3.5" />
              Create new team
            </Button>
          </li>
        </ul>
      </div>

      {selectedTeamId && (
        <AddTeamMemberDialog
          open={isAddMemberDialogOpen}
          onOpenChange={setIsAddMemberDialogOpen}
          onAddMember={handleAddMember}
          teamId={selectedTeamId}
        />
      )}
    </>
  );
};

export default TeamsSection;
